import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatPKR, pkrToPaisa } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { buildPurchase, normalizeImei } from './purchasePayload'
import { savePurchase } from './savePurchase'
import { ImeiScanner } from '@/features/scanner/ImeiScanner'

const BRANDS = [
  { value: '', label: 'Brand (optional)' },
  { value: 'samsung', label: 'Samsung' },
  { value: 'oneplus', label: 'OnePlus' },
  { value: 'apple', label: 'Apple' },
  { value: 'google', label: 'Google Pixel' },
]

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'
const labelCls = 'mb-1 block text-xs font-medium text-slate-400'

export function AddPurchasePage() {
  const ws = getActiveWorkspaceId()
  const navigate = useNavigate()

  const categories = useLiveQuery(
    async () =>
      ws
        ? openWorkspaceDb(ws)
            .categories.where('workspace_id')
            .equals(ws)
            .filter((c) => !c.archived_at && !c.deleted_at)
            .toArray()
        : [],
    [ws],
  )

  const [categoryId, setCategoryId] = useState('')
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [imei1, setImei1] = useState('')
  const [imei2, setImei2] = useState('')
  const [storage, setStorage] = useState('')
  const [ram, setRam] = useState('')
  const [condition, setCondition] = useState('')
  const [color, setColor] = useState('')
  const [cost, setCost] = useState('')
  const [direct, setDirect] = useState('')
  const [pta, setPta] = useState<'approved' | 'non_pta' | 'unknown'>('unknown')
  const [carrier, setCarrier] = useState<'unlocked' | 'carrier_locked' | 'unknown'>('unknown')
  const [mdm, setMdm] = useState<'not_observed' | 'enrolled' | 'unknown'>('not_observed')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [scanOpen, setScanOpen] = useState(false)
  const [scanTarget, setScanTarget] = useState<'imei1' | 'imei2'>('imei1')
  const [scanned, setScanned] = useState<'imei1' | 'imei2' | null>(null)
  const scannedTimer = useRef<number | null>(null)

  function openScan(field: 'imei1' | 'imei2') {
    setScanTarget(field)
    setScanOpen(true)
  }

  /** Fill the targeted field and flash a green "scanned" indicator (scanner auto-closed). */
  function handleFill(imei: string) {
    const field = scanTarget
    if (field === 'imei1') setImei1(imei)
    else setImei2(imei)
    setScanned(field)
    if (scannedTimer.current !== null) window.clearTimeout(scannedTimer.current)
    scannedTimer.current = window.setTimeout(() => setScanned(null), 2500)
  }

  // Default the category to Mobile (the default starter category) once categories load.
  useEffect(() => {
    if (categoryId) return
    const list = categories ?? []
    if (list.length === 0) return
    const mobile =
      list.find((c) => c.seed_key === 'starter_mobile') ??
      [...list].sort((a, b) => a.sort_order - b.sort_order)[0]
    setCategoryId(mobile.id)
  }, [categories, categoryId])

  const isApple = brand === 'apple'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!ws) return setError('No active workspace. Sign in again.')
    if (!categoryId) return setError('Select a category.')
    if (!model.trim()) return setError('Model is required.')

    const n1 = normalizeImei(imei1)
    const n2 = normalizeImei(imei2)
    for (const [label, n] of [
      ['IMEI 1', n1],
      ['IMEI 2', n2],
    ] as const) {
      if (n && (n.length < 14 || n.length > 17)) return setError(`${label} must be 14-17 digits.`)
    }

    let costPaisa: number
    let directPaisa = 0
    try {
      costPaisa = pkrToPaisa(cost)
    } catch {
      return setError('Enter a valid purchase cost (PKR).')
    }
    if (direct.trim()) {
      try {
        directPaisa = pkrToPaisa(direct)
      } catch {
        return setError('Enter a valid direct cost (PKR).')
      }
    }

    const built = buildPurchase({
      workspace_id: ws,
      category_id: categoryId,
      brand_code: brand || null,
      model,
      storage: storage || null,
      ram: ram || null,
      condition: condition || null,
      color: color || null,
      pta_status: pta,
      carrier_lock_status: isApple ? carrier : null,
      mdm_status: isApple ? mdm : null,
      imeis: [n1, n2].filter(Boolean),
      cost_paisa: costPaisa,
      direct_cost_paisa: directPaisa,
    })

    setBusy(true)
    try {
      const summary = await savePurchase(ws, built)
      if (summary.rejected.length > 0) {
        const fe = summary.rejected[0].result.field_errors?.[0]
        setError(
          `Not saved to server: ${summary.rejected[0].result.status}${fe ? ` — ${fe.field}: ${fe.message}` : ''}. The local record is kept; fix and retry.`,
        )
        return
      }
      navigate('/inventory')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page title="Add purchase" note="Saved offline first, then synced. Money is integer paisa.">
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className={labelCls} htmlFor="category">Category</label>
          <select id="category" className={inputCls} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Select…</option>
            {(categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="brand">Brand</label>
            <select id="brand" className={inputCls} value={brand} onChange={(e) => setBrand(e.target.value)}>
              {BRANDS.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="model">Model *</label>
            <input id="model" className={inputCls} value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. iPhone 13" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="imei1">IMEI 1</label>
            <div className="relative">
              <input
                id="imei1"
                className={`${inputCls} pr-10 ${scanned === 'imei1' ? 'border-emerald-500 ring-1 ring-emerald-500' : ''}`}
                inputMode="numeric"
                value={imei1}
                onChange={(e) => setImei1(e.target.value)}
                placeholder="15 digits"
              />
              <FieldScanButton onClick={() => openScan('imei1')} label="Scan IMEI 1" />
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="imei2">IMEI 2 (optional)</label>
            <div className="relative">
              <input
                id="imei2"
                className={`${inputCls} pr-10 ${scanned === 'imei2' ? 'border-emerald-500 ring-1 ring-emerald-500' : ''}`}
                inputMode="numeric"
                value={imei2}
                onChange={(e) => setImei2(e.target.value)}
                placeholder="optional"
              />
              <FieldScanButton onClick={() => openScan('imei2')} label="Scan IMEI 2" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className={labelCls} htmlFor="storage">Storage</label>
            <input id="storage" className={inputCls} value={storage} onChange={(e) => setStorage(e.target.value)} placeholder="128GB" />
          </div>
          <div>
            <label className={labelCls} htmlFor="ram">RAM</label>
            <input id="ram" className={inputCls} value={ram} onChange={(e) => setRam(e.target.value)} placeholder="8GB" />
          </div>
          <div>
            <label className={labelCls} htmlFor="condition">Condition</label>
            <input id="condition" className={inputCls} value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="A grade" />
          </div>
          <div>
            <label className={labelCls} htmlFor="color">Color</label>
            <input id="color" className={inputCls} value={color} onChange={(e) => setColor(e.target.value)} placeholder="Black" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="cost">Purchase cost (PKR) *</label>
            <input id="cost" className={inputCls} inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="100000" />
          </div>
          <div>
            <label className={labelCls} htmlFor="direct">Extra cost on this phone (PKR)</label>
            <input id="direct" className={inputCls} inputMode="decimal" value={direct} onChange={(e) => setDirect(e.target.value)} placeholder="optional" />
            <p className="mt-1 text-[11px] leading-snug text-slate-500">
              Is handset par aane wala extra kharcha (repair parts, PTA fee, box/accessories). Yeh cost
              mein jud kar profit nikalta hai.
            </p>
          </div>
        </div>

        <div>
          <label className={labelCls} htmlFor="pta">PTA status</label>
          <select id="pta" className={inputCls} value={pta} onChange={(e) => setPta(e.target.value as typeof pta)}>
            <option value="unknown">Unknown / Not checked</option>
            <option value="approved">PTA Approved</option>
            <option value="non_pta">Non PTA</option>
          </select>
        </div>

        {isApple ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor="carrier">Carrier lock</label>
              <select id="carrier" className={inputCls} value={carrier} onChange={(e) => setCarrier(e.target.value as typeof carrier)}>
                <option value="unknown">Unknown</option>
                <option value="unlocked">Factory Unlock</option>
                <option value="carrier_locked">JV / Carrier Locked</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="mdm">MDM</label>
              <select id="mdm" className={inputCls} value={mdm} onChange={(e) => setMdm(e.target.value as typeof mdm)}>
                <option value="not_observed">No MDM observed</option>
                <option value="enrolled">MDM enrolled</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
          </div>
        ) : null}

        {cost ? (
          <p className="text-xs text-slate-400">
            Cost: <span className="tabular-nums text-slate-200">{safeFormat(cost)}</span>
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-xl bg-sky-500 px-4 py-3 text-base font-semibold text-white disabled:opacity-60"
        >
          {busy ? 'Saving…' : 'Save purchase'}
        </button>

        <ImeiScanner
          open={scanOpen}
          onClose={() => setScanOpen(false)}
          onFill={handleFill}
          targetLabel={scanTarget === 'imei1' ? 'IMEI 1' : 'IMEI 2'}
        />
      </form>
    </Page>
  )
}

function safeFormat(raw: string): string {
  try {
    return formatPKR(pkrToPaisa(raw))
  } catch {
    return '—'
  }
}

/** Small scan icon placed inside an IMEI field. */
function FieldScanButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-sky-400 hover:bg-slate-800"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="h-5 w-5" aria-hidden="true">
        <path d="M3 7V4h3M21 7V4h-3M3 17v3h3M21 17v3h-3M7 12h10" />
      </svg>
    </button>
  )
}
